import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApiError } from '@/modules/common/hooks/use-api-error';

import { createWorklog, deleteWorklog, updateWorklog } from '../api/time-tracking.api';
import {
  CreateWorklogRequest,
  UpdateWorklogRequest,
  WorklogResponse,
} from '../types/dto/worklogs-summary.dto';
import { invalidateTimeTrackingQueries } from '../utils/time-tracking-query-invalidation';

// Note: worklog mutations do NOT invalidate the active timer. The timer is a
// separate backend resource that a worklog create/edit/delete never changes, so
// re-fetching `/timer` here would be wasted load (OPS-1538 load review). Timer
// surfaces stay in sync via the timer mutations' own setQueryData.

export const useCreateWorklog = () => {
  const queryClient = useQueryClient();

  return useMutation<WorklogResponse, Error, CreateWorklogRequest>({
    mutationFn: (values: CreateWorklogRequest) => createWorklog(values),
    onSuccess: () => invalidateTimeTrackingQueries(queryClient),
  });
};

export const useUpdateWorklog = () => {
  const queryClient = useQueryClient();

  return useMutation<WorklogResponse, Error, UpdateWorklogRequest>({
    mutationFn: (values: UpdateWorklogRequest) => updateWorklog(values),
    onSuccess: () => invalidateTimeTrackingQueries(queryClient),
  });
};

export const useDeleteWorklog = () => {
  const queryClient = useQueryClient();
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: (id: number) => deleteWorklog(id),
    onSuccess: () => invalidateTimeTrackingQueries(queryClient),
    onError: (error: Error) =>
      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.timeTracking:modal.deleteEntry.error'
      ),
  });
};
